import React, { useEffect, useMemo, useState } from 'react';
import { TVs } from '../services/api';
import TvEditorModal from './TvEditorModal';

export default function TvsSection({ groups }) {
  const [tvs, setTvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [gFilter, setGFilter] = useState('');
  const [tFilter, setTFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const groupMap = useMemo(() => Object.fromEntries((groups||[]).map(x => [x._id, x.nombre])), [groups]);

  const fetchTvs = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await TVs.getTvs();
      setTvs(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTvs(); }, [fetchTvs]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return tvs
      .filter(tv => !term || tv.nombre?.toLowerCase().includes(term))
      .filter(tv => !gFilter || String(tv.grupoId || '') === gFilter)
      .filter(tv => !tFilter || tv.tipo === tFilter)
      .sort((a, b) => new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0));
  }, [tvs, q, gFilter, tFilter]);

  const onNew = () => { setEditing(null); setOpen(true); };
  const onEdit = (tv) => { setEditing(tv); setOpen(true); };

  const removeTv = async (tv) => {
    if (!window.confirm(`¿Eliminar la pantalla "${tv.nombre}"?`)) return;
    try { await TVs.deleteTv(tv._id); fetchTvs(); }
    catch (e) { console.error(e); alert(e.message); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 pb-16">
      {/* Toolbar de TVs */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar pantalla..."
          className="w-full sm:w-80 rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60"
        />
        <select value={gFilter} onChange={e => setGFilter(e.target.value)} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
          <option value="">Todos los grupos</option>
          {groups?.map(g => (<option key={g._id} value={g._id}>{g.nombre}</option>))}
        </select>
        <select value={tFilter} onChange={e => setTFilter(e.target.value)} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
          <option value="">Todos los tipos</option>
          <option value="colas">colas</option>
          <option value="publicidad">publicidad</option>
        </select>
        <div className="flex-1" />
        <button onClick={onNew} className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm font-medium">Nueva pantalla</button>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-white/70">Cargando pantallas…</div>
      ) : error ? (
        <div className="text-red-400">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-white/60">No hay pantallas.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Nombre</th>
                <th className="text-left px-4 py-2 font-semibold">Tipo</th>
                <th className="text-left px-4 py-2 font-semibold">Grupo</th>
                <th className="text-left px-4 py-2 font-semibold">Creado</th>
                <th className="text-right px-4 py-2 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(tv => (
                <tr key={tv._id} className="border-t border-white/10">
                  <td className="px-4 py-2">{tv.nombre}</td>
                  <td className="px-4 py-2"><span className="capitalize">{tv.tipo}</span></td>
                  <td className="px-4 py-2">{groupMap[String(tv.grupoId || '')] || '—'}</td>
                  <td className="px-4 py-2">{tv.fechaCreacion ? new Date(tv.fechaCreacion).toLocaleString() : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => onEdit(tv)} className="rounded-lg bg-white/10 px-2 py-1 hover:bg-white/20">Editar</button>
                      <button onClick={() => removeTv(tv)} className="rounded-lg bg-red-600/80 hover:bg-red-600 px-2 py-1">Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TvEditorModal
        open={open}
        onClose={() => setOpen(false)}
        draft={editing}
        groups={groups}
        onSaved={() => { setOpen(false); fetchTvs(); }}
      />
    </div>
  );
}
